import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSearch, FaTimes, FaCode, FaLayerGroup } from 'react-icons/fa';
import { db, collection, getDocs, analytics } from '../firebase';
import { logEvent } from 'firebase/analytics';
import { projects as constantProjects, experiences, technologies } from '../Constants';

const AdvancedSearch = ({ onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    fetchAllData();
  }, []);

  // Reset active index when results change
  useEffect(() => {
    setActiveIndex(-1);
  }, [results]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (results.length === 0) return;
      
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex(prev => Math.min(prev + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && activeIndex >= 0) {
        e.preventDefault();
        const selected = results[activeIndex];
        
        // Track selection
        if (analytics) {
          logEvent(analytics, 'select_item', { item_name: selected.title, item_category: selected.type });
        }
        
        onClose();
        // Fallback simple navigation logic
        if (selected.type === 'project') {
          window.location.href = '/projects';
        } else {
          window.location.href = `/#${selected.type}`;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [results, activeIndex, onClose]);

  const fetchAllData = async () => {
    try {
      // Fetch projects
      const projectsSnapshot = await getDocs(collection(db, 'projects'));
      const projects = projectsSnapshot.docs.map(doc => ({
        id: doc.id,
        type: 'project',
        ...doc.data()
      }));

      // Fetch content
      const contentSnapshot = await getDocs(collection(db, 'content'));
      const content = contentSnapshot.docs.map(doc => ({
        id: doc.id,
        type: 'content',
        ...doc.data()
      }));

      // Pull localized constant data
      const localProjects = constantProjects.map((p, i) => ({ id: `local-proj-${i}`, type: 'project', ...p }));
      
      const localContent = [
        ...experiences.map((e, i) => ({ id: `exp-${i}`, type: 'experience', title: e.title, subtitle: e.company_name, description: e.summary, tags: e.tags })),
        ...technologies.map((t, i) => ({ id: `tech-${i}`, type: 'skills', title: t.name, subtitle: 'Skill', description: `Proficient in ${t.name}` }))
      ];

      setAllData({ 
        projects: [...projects, ...localProjects], 
        content: [...content, ...localContent] 
      });
    } catch (error) {
      console.error('Error fetching search data:', error);
    }
  };

  const performSearch = (query) => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    setLoading(true);
    const lowerQuery = query.toLowerCase();
    let searchResults = [];

    // Search projects
    if (filter === 'all' || filter === 'projects') {
      const projectResults = allData.projects.filter(project => {
        const titleMatch = project.title?.toLowerCase().includes(lowerQuery);
        const descMatch = project.description?.toLowerCase().includes(lowerQuery);
        const tagsMatch = project.tags?.some(tag => tag.toLowerCase().includes(lowerQuery));
        const categoryMatch = project.category?.toLowerCase().includes(lowerQuery);
        
        return titleMatch || descMatch || tagsMatch || categoryMatch;
      });
      searchResults = [...searchResults, ...projectResults];
    }

    // Search content
    if (filter === 'all' || filter === 'content') {
      const contentResults = allData.content.filter(item => {
        const titleMatch = item.title?.toLowerCase().includes(lowerQuery);
        const subtitleMatch = item.subtitle?.toLowerCase().includes(lowerQuery);
        const bioMatch = item.bio?.toLowerCase().includes(lowerQuery);
        
        return titleMatch || subtitleMatch || bioMatch;
      });
      searchResults = [...searchResults, ...contentResults];
    }

    setResults(searchResults);
    setLoading(false);
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      performSearch(searchQuery);
      
      // Fire analytics search event if query is meaningful
      if (analytics && searchQuery.trim().length >= 2) {
        logEvent(analytics, 'search', { search_term: searchQuery.trim() });
      }
    }, 400);

    return () => clearTimeout(debounce);
  }, [searchQuery, filter]);

  const highlightText = (text, query) => {
    if (!query || !text) return text;
    
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, i) => 
      part.toLowerCase() === query.toLowerCase() ? 
        <mark key={i} className="bg-violet-500/30 text-white">{part}</mark> : part
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -50, opacity: 0 }}
        className="bg-gray-900 rounded-2xl shadow-2xl w-full max-w-3xl mt-20 mb-20"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Header */}
        <div className="p-6 border-b border-gray-800">
          <div className="flex items-center gap-4">
            <FaSearch className="text-violet-400 text-xl" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search projects, skills, content..."
              className="flex-1 bg-transparent text-white text-lg outline-none"
              autoFocus
            />
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-800 rounded-lg transition-all"
            >
              <FaTimes className="text-gray-400" />
            </button>
          </div>

          {/* Filters */}
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg text-sm transition-all ${
                filter === 'all'
                  ? 'bg-violet-500 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('projects')}
              className={`px-4 py-2 rounded-lg text-sm transition-all ${
                filter === 'projects'
                  ? 'bg-violet-500 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              Projects
            </button>
            <button
              onClick={() => setFilter('content')}
              className={`px-4 py-2 rounded-lg text-sm transition-all ${
                filter === 'content'
                  ? 'bg-violet-500 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              Content
            </button>
          </div>
        </div>

        {/* Results */}
        <div className="max-h-96 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500 mx-auto"></div>
            </div>
          ) : results.length > 0 ? (
            <div className="p-4 space-y-2">
              {results.map((result, index) => (
                <motion.a
                  key={`${result.type}-${result.id}-${index}`}
                  href={result.type === 'project' ? `/projects` : `/#${result.type}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`block p-4 rounded-lg transition-all ${activeIndex === index ? 'bg-violet-600/30 border border-violet-500/50' : 'bg-gray-800 hover:bg-gray-700'}`}
                  onClick={() => {
                    if (analytics) {
                      logEvent(analytics, 'select_item', { item_name: result.title, item_category: result.type });
                    }
                    onClose();
                  }}
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-violet-500/20 rounded-lg">
                      {result.type === 'project' ? (
                        <FaCode className="text-violet-400" />
                      ) : (
                        <FaLayerGroup className="text-violet-400" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-white font-semibold">
                        {highlightText(result.title, searchQuery)}
                      </h3>
                      <p className="text-gray-400 text-sm mt-1 line-clamp-2">
                        {highlightText(result.description || result.bio || result.subtitle, searchQuery)}
                      </p>
                      {result.tags && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {result.tags.slice(0, 3).map((tag, i) => (
                            <span
                              key={i}
                              className="text-xs px-2 py-1 bg-violet-500/20 text-violet-300 rounded"
                            >
                              {highlightText(tag, searchQuery)}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.a>
              ))}
            </div>
          ) : searchQuery ? (
            <div className="p-8 text-center text-gray-400">
              <p>No results found for "{searchQuery}"</p>
              <p className="text-sm mt-2">Try different keywords or filters</p>
            </div>
          ) : (
            <div className="p-8 text-center text-gray-400">
              <p>Start typing to search...</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-800 flex items-center justify-between text-xs text-gray-500">
          <div>
            {results.length > 0 && (
              <span>{results.length} result{results.length !== 1 ? 's' : ''} found</span>
            )}
          </div>
          <div className="flex gap-4">
            <span><kbd className="px-2 py-1 bg-gray-800 rounded">↑↓</kbd> Navigate</span>
            <span><kbd className="px-2 py-1 bg-gray-800 rounded">Enter</kbd> Select</span>
            <span><kbd className="px-2 py-1 bg-gray-800 rounded">Esc</kbd> Close</span>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AdvancedSearch;
